import { autoUpdate, computePosition, flip, offset, shift } from "@floating-ui/dom"
import { debounce, nextFrame } from "../../helpers"
import { editorFromClosestRichTextArea } from "./utils"

class RichTextToolbarElement extends HTMLElement {
  constructor() {
    super()
    this.popover = "manual"
    this.update = debounce(this.update.bind(this), 100)
  }

  async connectedCallback() {
    this.editor = await editorFromClosestRichTextArea(this)
    if (!this.isConnected) return
    this.editor.on("transaction", this.update)
    this.addEventListener("mousedown", this.handleClick)
    this.addEventListener("keydown", this.handleKey)
  }

  disconnectedCallback() {
    this.editor?.off("transaction", this.update)
    this.removeEventListener("mousedown", this.handleClick)
    this.removeEventListener("keydown", this.handleKey)
    this.hide()
  }

  // Event handlers

  handleClick = (event) => {
    const button = event.target.closest("[data-command]:not([data-input])")
    if (!button) return
    event.preventDefault()
    this.perform(button.getAttribute("data-command"))
  }

  handleKey = (event) => {
    const handler = this.keyHandlers[event.key]
    if (handler) handler(event)
  }

  keyHandlers = {
    Escape: () => {
      this.hide()
    },
    Enter: (event) => {
      event.preventDefault()
      const input = event.target.closest("[data-input][data-command]")
      if (input) this.perform(input.getAttribute("data-command"))
    },
  }

  // Rendering

  async update() {
    await nextFrame()
    if (this.isFocused) return
    this.hide()
    if (!this.shouldShow) return
    this.updateButtons()
    this.show()
  }

  updateButtons() {
    for (const button of this.buttons) {
      const name = button.getAttribute("data-name")
      const command = button.getAttribute("data-command")
      button.setAttribute("aria-pressed", name && this.editor.isActive(name))
      button.disabled = !this.canPerform(command)
    }
  }

  show() {
    this.showPopover()

    const contextElement = this.editor.element
    const getBoundingClientRect = () => this.editor.selectionRect
    const reference = { contextElement, getBoundingClientRect }

    const middleware = [offset(8), flip(), shift({ padding: 16 })]
    const options = { middleware, placement: "top" }

    this.cleanup = autoUpdate(reference, this, async () => {
      const { x, y } = await computePosition(reference, this, options)
      Object.assign(this.style, { left: `${x}px`, top: `${y}px` })
    })
  }

  hide() {
    this.hidePopover()
    this.closeDialogs()
    this.cleanup?.()
    this.cleanup = null
  }

  // Commands

  canPerform(command) {
    return this.editor.can().chain().focus()[command]().run()
  }

  perform(command) {
    if (typeof this[command] == "function") {
      this[command]()
    } else {
      this.editor.chain().focus()[command]().run()
    }
  }

  toggleLink() {
    this.openDialog("link")
    const input = this.getInput("link")
    input.value = this.editor.getAttributes("link").href || ""
    input.focus()
  }

  setLink() {
    const href = this.getInput("link").value
    if (!href) return this.unsetLink()
    this.editor.chain().focus().extendMarkRange("link").setLink({ href }).run()
    this.closeDialog("link")
  }

  unsetLink() {
    this.editor.chain().focus().extendMarkRange("link").unsetLink().run()
    this.closeDialog("link")
  }

  // Dialogs

  openDialog(name) {
    this.getDialog(name).open = true
  }

  closeDialog(name) {
    this.getDialog(name).open = false
  }

  closeDialogs() {
    for (const dialog of this.dialogs) dialog.open = false
  }

  getDialog(name) {
    return this.querySelector(`[data-dialog="${name}"]`)
  }

  getInput(name) {
    return this.querySelector(`[data-input="${name}"]`)
  }

  // Properties

  get buttons() {
    return [...this.querySelectorAll("[data-command]")]
  }

  get dialogs() {
    return [...this.querySelectorAll("[data-dialog]")]
  }

  get isFocused() {
    return this.contains(document.activeElement)
  }

  get shouldShow() {
    return this.editor.isFocused && !this.editor.selection.empty
  }
}

customElements.define("rich-text-toolbar", RichTextToolbarElement)
